/**
 * Panel de ranking por puntajes de actividades.
 *
 * Suma todos los `clubActivityScores` agrupados por club y por categoría.
 * Muestra tabla con totales y desglose por categoría.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  Modal,
  ModalFooter,
} from "@/components/ui";
import {
  Trophy,
  Medal,
  Download,
  FileDown,
  ChevronDown,
  ChevronRight,
  Info,
  HelpCircle,
  Sparkles,
  Crown,
} from "lucide-react";
import type {
  ActivityScoreConfig,
  ActivityScoreInput,
  ActivityScoreMode,
  ClubActivityScore,
  EventActivity,
  EventActivityCategory,
} from "@/types";
import { exportToXlsx } from "@/lib/utils/exportXlsx";
import { downloadActivityResultsBookPdf } from "@/lib/pdf/activityResultsPdf";
import {
  SCORE_MODE_DESCRIPTION,
  SCORE_MODE_LABEL,
  formatActivityResult,
  formatActivityRubric,
} from "@/lib/utils/activityScoreFormat";

interface ActivityRow {
  activityId: string;
  name: string;
  points: number;
  count: number;
  /** Modo de puntaje de la actividad (para mostrar leyenda). */
  scoreMode?: ActivityScoreMode;
  scoreConfig?: ActivityScoreConfig;
  /** Último resultado registrado (input del jurado) — usado para mostrar etiqueta legible. */
  lastInput?: ActivityScoreInput;
}

interface CategoryRow {
  name: string;
  points: number;
  count: number;
  byActivity: Map<string, ActivityRow>;
}

interface ClubRow {
  clubId: string;
  clubName: string;
  total: number;
  byCategory: Map<string, CategoryRow>;
  scoreCount: number;
  /**
   * Posición usando "dense ranking": si N clubes empatan en puntaje, todos
   * comparten la misma posición y el siguiente puntaje toma la posición
   * inmediata siguiente (1,1,1,2,2,3...). Pensado para que pueda haber
   * varios "primeros", varios "segundos", etc.
   */
  position: number;
  /** Cuántos clubes comparten esta posición (para mostrar el empate). */
  tiedWith: number;
}

interface Props {
  eventId: string;
  eventName: string;
  scores: ClubActivityScore[];
  clubs: Array<{ id: string; name: string }>;
  categories: EventActivityCategory[];
  activities: EventActivity[];
  isAdmin?: boolean;
  /**
   * Si se define, el panel muestra ÚNICAMENTE la fila de este club (con su
   * posición global), ocultando el resto de clubes y la exportación. Pensado
   * para que un director de club solo vea su propia información en el ranking.
   */
  restrictToClubId?: string;
}

export function EventActivityRankingPanel({
  eventName,
  scores,
  clubs,
  categories,
  activities,
  isAdmin = false,
  restrictToClubId,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedActivityId, setSelectedActivityId] = useState<string>("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);

  const activityById = useMemo(() => {
    const map = new Map<string, EventActivity>();
    (activities ?? []).forEach((a) => map.set(a.id, a));
    return map;
  }, [activities]);

  const rows = useMemo<ClubRow[]>(() => {
    const map = new Map<string, ClubRow>();
    // sembrar con clubes inscritos
    clubs.forEach((c) => {
      map.set(c.id, {
        clubId: c.id,
        clubName: c.name,
        total: 0,
        byCategory: new Map(),
        scoreCount: 0,
        position: 0,
        tiedWith: 1,
      });
    });
    scores.forEach((s) => {
      let row = map.get(s.clubId);
      if (!row) {
        row = {
          clubId: s.clubId,
          clubName: s.clubName,
          total: 0,
          byCategory: new Map(),
          scoreCount: 0,
          position: 0,
          tiedWith: 1,
        };
        map.set(s.clubId, row);
      }
      row.total += s.points;
      row.scoreCount += 1;
      const category = row.byCategory.get(s.categoryId) ?? {
        name: s.categoryName,
        points: 0,
        count: 0,
        byActivity: new Map<string, ActivityRow>(),
      };
      category.points += s.points;
      category.count += 1;
      const act = activityById.get(s.activityId);
      const activity = category.byActivity.get(s.activityId) ?? {
        activityId: s.activityId,
        name: s.activityName,
        points: 0,
        count: 0,
        scoreMode: act?.scoreMode,
        scoreConfig: act?.scoreConfig,
        lastInput: undefined,
      };
      activity.points += s.points;
      activity.count += 1;
      activity.lastInput = s.input;
      if (!activity.scoreMode && act) {
        activity.scoreMode = act.scoreMode;
        activity.scoreConfig = act.scoreConfig;
      }
      category.byActivity.set(s.activityId, activity);
      row.byCategory.set(s.categoryId, category);
    });

    // Ordenar por puntaje desc, desempatando por nombre asc para estabilidad
    // (no para asignar posiciones distintas).
    const sorted = Array.from(map.values()).sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.clubName.localeCompare(b.clubName, "es");
    });

    // Dense ranking: clubes con el mismo total comparten posición.
    let currentPos = 0;
    let lastTotal: number | null = null;
    sorted.forEach((row) => {
      if (row.total !== lastTotal) {
        currentPos += 1;
        lastTotal = row.total;
      }
      row.position = currentPos;
    });

    // Calcular cuántos clubes comparten cada posición.
    const tiedCount = new Map<number, number>();
    sorted.forEach((row) => {
      tiedCount.set(row.position, (tiedCount.get(row.position) ?? 0) + 1);
    });
    sorted.forEach((row) => {
      row.tiedWith = tiedCount.get(row.position) ?? 1;
    });

    return sorted;
  }, [scores, clubs, activityById]);

  const sortedCategories = useMemo(
    () => [...(categories ?? [])].sort((a, b) => a.order - b.order),
    [categories],
  );

  const sortedActivities = useMemo(() => {
    const catOrder = new Map<string, number>();
    sortedCategories.forEach((c, i) => catOrder.set(c.id, i));
    return [...(activities ?? [])].sort((a, b) => {
      const ca = catOrder.get(a.categoryId) ?? Number.MAX_SAFE_INTEGER;
      const cb = catOrder.get(b.categoryId) ?? Number.MAX_SAFE_INTEGER;
      if (ca !== cb) return ca - cb;
      return a.order - b.order;
    });
  }, [activities, sortedCategories]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    sortedCategories.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [sortedCategories]);

  /** Modos de puntaje presentes en este evento (para la leyenda). */
  const usedScoreModes = useMemo<ActivityScoreMode[]>(() => {
    const set = new Set<ActivityScoreMode>();
    (activities ?? []).forEach((a) => set.add(a.scoreMode));
    const order: ActivityScoreMode[] = [
      "podium",
      "pass_fail",
      "fixed",
      "timing",
      "deductive",
      "accumulative",
    ];
    return order.filter((m) => set.has(m));
  }, [activities]);

  const scoresByActivity = useMemo(() => {
    const map = new Map<string, ClubActivityScore[]>();
    scores.forEach((s) => {
      const arr = map.get(s.activityId) ?? [];
      arr.push(s);
      map.set(s.activityId, arr);
    });
    return map;
  }, [scores]);

  useEffect(() => {
    if (!selectedCategoryId && sortedCategories.length > 0) {
      setSelectedCategoryId(sortedCategories[0].id);
    }
  }, [selectedCategoryId, sortedCategories]);

  useEffect(() => {
    if (!selectedActivityId && sortedActivities.length > 0) {
      setSelectedActivityId(sortedActivities[0].id);
    }
  }, [selectedActivityId, sortedActivities]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const exportCsv = () => {
    exportToXlsx({
      filename: `ranking-actividades-${Date.now()}`,
      sheetName: "Ranking actividades",
      rows,
      columns: [
        { header: "Posicion", key: (r) => r.position, width: 10 },
        {
          header: "Empate",
          key: (r) => (r.tiedWith > 1 ? `empate x${r.tiedWith}` : ""),
          width: 12,
        },
        { header: "Club", key: (r) => r.clubName, width: 30 },
        { header: "Puntaje total", key: (r) => r.total, width: 14 },
        { header: "# Actividades", key: (r) => r.scoreCount, width: 14 },
      ],
    });
  };

  const exportAllActivitiesPdf = () => {
    const sections = sortedActivities.map((act) => ({
      categoryName: categoryNameById.get(act.categoryId) ?? "Sin categoría",
      activity: {
        id: act.id,
        name: act.name,
        scoreMode: act.scoreMode,
        scoreConfig: act.scoreConfig,
      },
      scores: scoresByActivity.get(act.id) ?? [],
    }));
    downloadActivityResultsBookPdf({
      eventName,
      sections,
      filenameBase: `resultados-todas-actividades-${Date.now()}`,
    });
  };

  const exportCategoryPdf = () => {
    if (!selectedCategoryId) return;
    const category = sortedCategories.find((c) => c.id === selectedCategoryId);
    if (!category) return;
    const categoryActivities = sortedActivities.filter(
      (a) => a.categoryId === category.id,
    );
    const sections = categoryActivities.map((act) => ({
      categoryName: category.name,
      activity: {
        id: act.id,
        name: act.name,
        scoreMode: act.scoreMode,
        scoreConfig: act.scoreConfig,
      },
      scores: scoresByActivity.get(act.id) ?? [],
    }));
    downloadActivityResultsBookPdf({
      eventName,
      sections,
      filenameBase: `resultados-${category.name}-${Date.now()}`,
    });
  };

  const exportSingleActivityPdf = () => {
    if (!selectedActivityId) return;
    const activity = sortedActivities.find((a) => a.id === selectedActivityId);
    if (!activity) return;
    downloadActivityResultsBookPdf({
      eventName,
      sections: [
        {
          categoryName:
            categoryNameById.get(activity.categoryId) ?? "Sin categoría",
          activity: {
            id: activity.id,
            name: activity.name,
            scoreMode: activity.scoreMode,
            scoreConfig: activity.scoreConfig,
          },
          scores: scoresByActivity.get(activity.id) ?? [],
        },
      ],
      filenameBase: `resultados-${activity.name}-${Date.now()}`,
    });
  };

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          Aún no hay clubes inscritos ni puntajes registrados.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              {restrictToClubId
                ? "Tu posición en el ranking"
                : "Ranking de actividades"}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {usedScoreModes.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLegendOpen((v) => !v)}
                  title="Cómo se calculan los puntajes"
                >
                  <HelpCircle className="w-4 h-4 mr-1" />
                  {legendOpen ? "Ocultar leyenda" : "¿Cómo se calculan?"}
                </Button>
              )}
              {!restrictToClubId && (
                <>
                  <Button variant="secondary" size="sm" onClick={exportCsv}>
                    <Download className="w-4 h-4 mr-1" />
                    Exportar Excel
                  </Button>
                  {isAdmin && sortedActivities.length > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setExportModalOpen(true)}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Exportar PDFs
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          {legendOpen && usedScoreModes.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
              <p className="font-medium flex items-center gap-1">
                <Info className="w-4 h-4" />
                Cómo se calculan los puntajes
              </p>
              <ul className="space-y-1.5 text-xs">
                {usedScoreModes.map((m) => (
                  <li key={m} className="leading-snug">
                    <span className="font-semibold">{SCORE_MODE_LABEL[m]}:</span>{" "}
                    <span className="text-blue-800">
                      {SCORE_MODE_DESCRIPTION[m]}
                    </span>
                  </li>
                ))}
                <li className="leading-snug text-blue-800">
                  <span className="font-semibold">Empates:</span> varios clubes
                  pueden compartir la misma posición; el siguiente puesto se
                  asigna inmediatamente después (1°, 1°, 1°, 2°...).
                </li>
              </ul>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {rows.map((r) => {
            // Director: solo su propia fila (con su posición global real).
            if (restrictToClubId && r.clubId !== restrictToClubId) return null;
            const isOpen = expanded.has(r.clubId) || !!restrictToClubId;
            const medalColor =
              r.position === 1
                ? "text-amber-500"
                : r.position === 2
                  ? "text-gray-400"
                  : r.position === 3
                    ? "text-orange-700"
                    : "text-gray-300";
            const isDirectorView = !!restrictToClubId;
            return (
              <div key={r.clubId}>
                {isDirectorView ? (
                  // Encabezado destacado para vista de director.
                  <div className="relative p-5 bg-gradient-to-br from-purple-50 via-white to-amber-50 border-b border-purple-100 overflow-hidden animate-fade-in-scale">
                    {r.position <= 3 && (
                      <>
                        <span className="pointer-events-none absolute top-2 right-4 text-amber-400 animate-float">
                          <Sparkles className="w-4 h-4" />
                        </span>
                        <span
                          className="pointer-events-none absolute bottom-3 right-12 text-amber-300 animate-float"
                          style={{ animationDelay: "0.6s" }}
                        >
                          <Sparkles className="w-3 h-3" />
                        </span>
                        <span
                          className="pointer-events-none absolute top-6 left-20 text-purple-300 animate-float"
                          style={{ animationDelay: "1.2s" }}
                        >
                          <Sparkles className="w-3 h-3" />
                        </span>
                      </>
                    )}
                    <div className="relative flex items-center gap-4 flex-wrap">
                      <div
                        className={
                          "flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-md border-2 " +
                          (r.position === 1
                            ? "border-amber-300 animate-glow"
                            : r.position <= 3
                              ? "border-purple-200 animate-bounce-in"
                              : "border-purple-200")
                        }
                      >
                        {r.position === 1 ? (
                          <Crown className="w-9 h-9 text-amber-500 animate-float" />
                        ) : r.position <= 3 ? (
                          <Medal className={`w-8 h-8 ${medalColor}`} />
                        ) : (
                          <span className="text-2xl font-bold text-purple-700">
                            {r.position}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-wide text-purple-700 font-medium">
                          Tu posición global
                        </p>
                        <p className="text-2xl font-bold text-gray-900 truncate">
                          {r.position}° lugar
                          {r.position === 1 && (
                            <span className="ml-2 text-xs text-amber-600 font-semibold animate-pulse">
                              ¡Líder!
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          <span className="text-sm text-gray-600">
                            {r.scoreCount} actividad(es) puntuada(s)
                          </span>
                          {r.tiedWith > 1 && (
                            <Badge
                              variant="default"
                              className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] animate-fade-in"
                            >
                              Empate · {r.tiedWith} clubes comparten este puesto
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-extrabold text-purple-700 tabular-nums">
                          <CountUp value={r.total} />
                        </p>
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          puntos totales
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 text-left"
                    onClick={() => toggle(r.clubId)}
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100">
                      {r.position <= 3 ? (
                        <Medal className={`w-5 h-5 ${medalColor}`} />
                      ) : (
                        <span className="text-sm font-semibold text-gray-600">
                          {r.position}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {r.clubName}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                        <span>{r.scoreCount} actividad(es) puntuada(s)</span>
                        {r.tiedWith > 1 && (
                          <Badge
                            variant="default"
                            className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]"
                          >
                            Empate · {r.tiedWith} clubes en puesto {r.position}
                          </Badge>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-700">
                        {r.total.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">pts</p>
                    </div>
                  </button>
                )}
                {isOpen && (
                  <div
                    className={
                      isDirectorView
                        ? "px-4 py-4 bg-white"
                        : "px-6 pb-4 bg-gray-50"
                    }
                  >
                    {r.byCategory.size === 0 && (
                      <p className="text-sm text-gray-500 py-2">
                        Sin puntajes registrados aún.
                      </p>
                    )}
                    {isDirectorView && r.byCategory.size > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        Desglose por categoría y actividad. Pasa el cursor sobre
                        la insignia del modo de puntaje para ver cómo se calculó.
                      </p>
                    )}
                    <div className={isDirectorView ? "space-y-3" : "space-y-1"}>
                      {Array.from(r.byCategory.values())
                        .sort((a, b) => b.points - a.points)
                        .map((category, idx) => (
                          <div
                            key={idx}
                            className="rounded-lg bg-white border border-gray-200 overflow-hidden"
                          >
                            <div
                              className={
                                "flex items-center justify-between gap-2 px-3 py-2 " +
                                (isDirectorView
                                  ? "bg-purple-50 border-b border-purple-100"
                                  : "")
                              }
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Badge variant="default">{category.count}</Badge>
                                <span className="text-sm font-semibold text-gray-800 truncate">
                                  {category.name}
                                </span>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="font-bold text-purple-700">
                                  {category.points.toLocaleString()}
                                </span>
                                <span className="text-[10px] text-gray-500 ml-1">
                                  pts
                                </span>
                              </div>
                            </div>
                            <div
                              className={
                                isDirectorView
                                  ? "p-2 grid gap-2 sm:grid-cols-2"
                                  : "px-3 pb-2 space-y-1"
                              }
                            >
                              {Array.from(category.byActivity.values())
                                .sort((a, b) => b.points - a.points)
                                .map((activity, activityIdx) =>
                                  isDirectorView ? (
                                    <div
                                      key={activityIdx}
                                      className="animate-fade-in-up animate-fill-both"
                                      style={{
                                        animationDelay: `${Math.min(activityIdx * 60, 600)}ms`,
                                      }}
                                    >
                                      <ActivityResultCard activity={activity} />
                                    </div>
                                  ) : (
                                    <ActivityResultRow
                                      key={activityIdx}
                                      activity={activity}
                                    />
                                  ),
                                )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      {isAdmin && !restrictToClubId && (
        <Modal
          isOpen={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          title="Exportar PDFs de resultados"
          description="Listos para lectura en premiación: una página por actividad."
          size="lg"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
              <p className="text-sm font-medium text-purple-900">
                Exportación general
              </p>
              <p className="text-xs text-purple-700 mt-1 mb-3">
                Incluye todas las actividades del evento en un solo PDF.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  exportAllActivitiesPdf();
                  setExportModalOpen(false);
                }}
                disabled={sortedActivities.length === 0}
              >
                <FileDown className="w-4 h-4 mr-1" />
                Descargar PDF completo ({sortedActivities.length} actividades)
              </Button>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-900">Por categoría</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  {sortedCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    exportCategoryPdf();
                    setExportModalOpen(false);
                  }}
                  disabled={!selectedCategoryId}
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  Descargar PDF categoría
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-900">Por actividad</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm min-w-[260px]"
                  value={selectedActivityId}
                  onChange={(e) => setSelectedActivityId(e.target.value)}
                >
                  {sortedActivities.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(categoryNameById.get(a.categoryId) ?? "Sin categoría") +
                        " · " +
                        a.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    exportSingleActivityPdf();
                    setExportModalOpen(false);
                  }}
                  disabled={!selectedActivityId}
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  Descargar PDF actividad
                </Button>
              </div>
            </div>
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setExportModalOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Card>
  );
}

// -------------------------------------------------------------------------
// Subcomponentes: presentación de una actividad puntuada.
// -------------------------------------------------------------------------

interface ActivityCellProps {
  activity: ActivityRow;
}

function ActivityResultRow({ activity }: ActivityCellProps) {
  const mode = activity.scoreMode;
  const result = mode
    ? formatActivityResult(mode, activity.scoreConfig, activity.lastInput)
    : "";
  const rubric = mode ? formatActivityRubric(mode, activity.scoreConfig) : "";
  return (
    <div
      className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 gap-2"
      title={rubric || undefined}
    >
      <div className="min-w-0 flex-1">
        <span className="truncate inline-block max-w-full align-middle">
          {activity.name}
        </span>
        {mode && (
          <span className="ml-1 text-[10px] text-gray-500">
            · {SCORE_MODE_LABEL[mode]}
          </span>
        )}
        {result && result !== "Sin registro" && (
          <span className="ml-1 text-[10px] text-purple-700">· {result}</span>
        )}
      </div>
      <span className="font-medium text-gray-800 shrink-0">
        {activity.points.toLocaleString()} pts
      </span>
    </div>
  );
}

function ActivityResultCard({ activity }: ActivityCellProps) {
  const mode = activity.scoreMode;
  const result = mode
    ? formatActivityResult(mode, activity.scoreConfig, activity.lastInput)
    : "";
  const rubric = mode ? formatActivityRubric(mode, activity.scoreConfig) : "";
  const modeLabel = mode ? SCORE_MODE_LABEL[mode] : "";
  return (
    <div className="rounded-md border border-gray-200 bg-white p-2.5 hover:border-purple-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 leading-tight">
            {activity.name}
          </p>
          {modeLabel && (
            <span
              className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5"
              title={rubric || undefined}
            >
              {modeLabel}
              {rubric && <Info className="w-3 h-3 opacity-70" />}
            </span>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-purple-700 leading-none tabular-nums">
            {activity.points.toLocaleString()}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-gray-500">
            pts
          </p>
        </div>
      </div>
      {result && result !== "Sin registro" && (
        <p className="mt-2 text-xs text-gray-700">
          <span className="text-gray-500">Resultado:</span>{" "}
          <span className="font-semibold text-gray-900">{result}</span>
        </p>
      )}
      {rubric && (
        <p className="mt-1 text-[10px] text-gray-500 leading-snug">
          <span className="font-medium">Puntos:</span> {rubric}
        </p>
      )}
    </div>
  );
}

/**
 * Anima un número de 0 a `value` en un breve lapso para dar sensación de
 * conteo dinámico. Pensado para destacar puntajes totales del club.
 */
function CountUp({ value, durationMs = 900 }: { value: number; durationMs?: number }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (value <= 0) {
      setDisplay(0);
      return;
    }
    const start = performance.now();
    let frame: number;
    const step = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / durationMs, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) {
        frame = requestAnimationFrame(step);
      }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, durationMs]);
  return <>{display.toLocaleString()}</>;
}
